import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "react-toastify";
import api from "../services/api";
import {
  Work,
  Add,
  Visibility,
  Edit,
  People,
  CheckCircle,
  Schedule,
} from "@mui/icons-material";

export default function Recruitment() {
  const navigate = useNavigate();
  const [jobs, setJobs] = useState([]);
  const [candidates, setCandidates] = useState([]);
  const [activeTab, setActiveTab] = useState("jobs");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (activeTab === "jobs") {
      fetchJobs();
    } else {
      fetchCandidates();
    }
  }, [activeTab]);

  const fetchJobs = async () => {
    try {
      setLoading(true);
      const response = await api.get("/recruitment/jobs");
      const jobsData = Array.isArray(response.data)
        ? response.data
        : Array.isArray(response.data?.data)
        ? response.data.data
        : [];
      setJobs(jobsData);
    } catch (err) {
      console.error("Failed to fetch jobs", err);
      toast.error("Failed to load job postings");
      setJobs([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchCandidates = async () => {
    try {
      setLoading(true);
      const response = await api.get("/recruitment/candidates");
      const candidatesData = Array.isArray(response.data)
        ? response.data
        : Array.isArray(response.data?.data)
        ? response.data.data
        : [];
      setCandidates(candidatesData);
    } catch (err) {
      console.error("Failed to fetch candidates", err);
      toast.error("Failed to load candidates");
      setCandidates([]);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      draft: "bg-gray-100 text-gray-700",
      published: "bg-green-100 text-green-700",
      closed: "bg-red-100 text-red-700",
      cancelled: "bg-gray-100 text-gray-700",
      applied: "bg-blue-100 text-blue-700",
      screening: "bg-yellow-100 text-yellow-700",
      shortlisted: "bg-purple-100 text-purple-700",
      interview_scheduled: "bg-orange-100 text-orange-700",
      interviewed: "bg-indigo-100 text-indigo-700",
      offer_sent: "bg-green-100 text-green-700",
      offer_accepted: "bg-green-100 text-green-700",
      rejected: "bg-red-100 text-red-700",
    };
    return badges[status] || badges.draft;
  };

  return (
    <div className="max-w-7xl mx-auto">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-8 flex items-center justify-between"
      >
        <div>
          <h1 className="text-4xl font-bold text-primary mb-2">
            Recruitment & Hiring
          </h1>
          <p className="text-gray-600 text-lg">
            Manage job postings and candidate applications
          </p>
        </div>
        {activeTab === "jobs" && (
          <button
            onClick={() => navigate("/recruitment/jobs/create")}
            className="btn-primary flex items-center gap-2"
          >
            <Add className="w-5 h-5" />
            Post New Job
          </button>
        )}
      </motion.div>

      {/* Tabs */}
      <div className="mb-6 flex gap-3 border-b border-gray-200">
        <button
          onClick={() => setActiveTab("jobs")}
          className={`px-6 py-3 font-semibold transition-all border-b-2 ${
            activeTab === "jobs"
              ? "border-primary text-primary"
              : "border-transparent text-gray-600 hover:text-primary"
          }`}
        >
          <Work className="inline mr-2" />
          Job Postings ({jobs.length})
        </button>
        <button
          onClick={() => setActiveTab("candidates")}
          className={`px-6 py-3 font-semibold transition-all border-b-2 ${
            activeTab === "candidates"
              ? "border-primary text-primary"
              : "border-transparent text-gray-600 hover:text-primary"
          }`}
        >
          <People className="inline mr-2" />
          Candidates ({candidates.length})
        </button>
      </div>

      {/* Jobs Tab */}
      {activeTab === "jobs" && (
        <>
          {loading ? (
            <div className="grid grid-cols-1 gap-4">
              {[1, 2, 3].map((i) => (
                <div key={i} className="card animate-pulse">
                  <div className="h-6 bg-gray-200 rounded w-1/3 mb-4"></div>
                  <div className="h-4 bg-gray-200 rounded w-1/2"></div>
                </div>
              ))}
            </div>
          ) : jobs.length === 0 ? (
            <div className="card text-center py-12">
              <Work className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600 text-lg mb-4">
                No job postings found
              </p>
              <button
                onClick={() => navigate("/recruitment/jobs/create")}
                className="btn-primary"
              >
                Create First Job Posting
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-4">
              {jobs.map((job, index) => (
                <motion.div
                  key={job._id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="card hover:shadow-soft-lg transition-all cursor-pointer"
                  onClick={() => navigate(`/recruitment/jobs/${job._id}`)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="text-xl font-bold text-primary">
                          {job.title}
                        </h3>
                        <span
                          className={`px-3 py-1 rounded-lg text-xs font-semibold ${getStatusBadge(
                            job.status
                          )}`}
                        >
                          {job.status.toUpperCase()}
                        </span>
                      </div>
                      <div className="flex items-center gap-4 text-sm text-gray-600 mb-2">
                        <span>
                          Department: {job.department_id?.name || "N/A"}
                        </span>
                        <span className="capitalize">
                          {job.employment_type}
                        </span>
                        {job.location && <span>📍 {job.location}</span>}
                      </div>
                      <div className="flex items-center gap-4 text-sm text-gray-600">
                        <span>👁️ {job.views_count || 0} views</span>
                        <span>
                          📝 {job.applications_count || 0} applications
                        </span>
                        <span>
                          Posted:{" "}
                          {new Date(job.posted_date).toLocaleDateString()}
                        </span>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/recruitment/jobs/${job._id}`);
                        }}
                        className="p-2 text-primary hover:bg-primary/10 rounded-xl transition-all"
                      >
                        <Visibility className="w-5 h-5" />
                      </button>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </>
      )}

      {/* Candidates Tab */}
      {activeTab === "candidates" && (
        <>
          {loading ? (
            <div className="grid grid-cols-1 gap-4">
              {[1, 2, 3].map((i) => (
                <div key={i} className="card animate-pulse">
                  <div className="h-6 bg-gray-200 rounded w-1/3 mb-4"></div>
                  <div className="h-4 bg-gray-200 rounded w-1/2"></div>
                </div>
              ))}
            </div>
          ) : candidates.length === 0 ? (
            <div className="card text-center py-12">
              <People className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600 text-lg mb-4">No candidates found</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-4">
              {candidates.map((candidate, index) => (
                <motion.div
                  key={candidate._id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="card hover:shadow-soft-lg transition-all cursor-pointer"
                  onClick={() =>
                    navigate(`/recruitment/candidates/${candidate._id}`)
                  }
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="text-xl font-bold text-primary">
                          {candidate.first_name} {candidate.last_name}
                        </h3>
                        <span
                          className={`px-3 py-1 rounded-lg text-xs font-semibold ${getStatusBadge(
                            candidate.status
                          )}`}
                        >
                          {candidate.status.replace(/_/g, " ").toUpperCase()}
                        </span>
                      </div>
                      <div className="flex items-center gap-4 text-sm text-gray-600 mb-2">
                        <span>📧 {candidate.email}</span>
                        {candidate.phone && <span>📞 {candidate.phone}</span>}
                        <span>
                          Applied:{" "}
                          {new Date(
                            candidate.application_date
                          ).toLocaleDateString()}
                        </span>
                      </div>
                      <div className="text-sm text-gray-600">
                        Position: {candidate.job_posting_id?.title || "N/A"}
                      </div>
                      {candidate.interviews?.length > 0 && (
                        <div className="mt-2 text-sm text-gray-600 flex items-center gap-1">
                          <Schedule className="w-4 h-4" />
                          {candidate.interviews.length} interview(s) scheduled
                        </div>
                      )}
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/recruitment/candidates/${candidate._id}`);
                        }}
                        className="p-2 text-primary hover:bg-primary/10 rounded-xl transition-all"
                      >
                        <Visibility className="w-5 h-5" />
                      </button>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
